import React, { useState, useEffect } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Listbox } from '@headlessui/react';
import { saveTheme, DEFAULT_FONT, DEFAULT_THEME } from '../utils/themeStorage';
import { applyTheme as applyThemeUtil } from '../utils/themeUtils';
import { Button, IconButton, PresetButton } from './ui';

/**
 * Color Presets
 *
 * Predefined color schemes for quick theme selection.
 * Each preset contains 4 coordinated colors: primary, secondary, accent, and neutral.
 */
const COLOR_PRESETS = {
  sage: {
    name: 'Sage & Brown (Default)',
    colors: {
      surface: '#ffffff',
      primary: '#8EA58C',
      secondary: '#5C4033',
      accent: '#BFCFBB',
      neutral: '#D9C2A8',
    },
  },
  rose: {
    name: 'Rose Gold Romance',
    colors: {
      surface: '#ffffff',
      primary: '#C8756F',
      secondary: '#5A3A37',
      accent: '#F0DCD9',
      neutral: '#E6D2C8',
    },
  },
  burgundy: {
    name: 'Burgundy & Ivory',
    colors: {
      surface: '#ffffff',
      primary: '#8B2E3F',
      secondary: '#2C2C2C',
      accent: '#D4B4B0',
      neutral: '#F8F6F3',
    },
  },
  dustyblue: {
    name: 'Dusty Blue & Blush',
    colors: {
      surface: '#ffffff',
      primary: '#8FA4B3',
      secondary: '#3F4A52',
      accent: '#E8C4C4',
      neutral: '#F0E8E8',
    },
  },
  terracotta: {
    name: 'Terracotta & Cream',
    colors: {
      surface: '#ffffff',
      primary: '#C97B63',
      secondary: '#4A3529',
      accent: '#E8C5B5',
      neutral: '#F5EFE7',
    },
  },
};

/**
 * Font Options
 * 
 * Popular font combinations for wedding websites
 */
const FONT_OPTIONS = [
  {
    name: 'System Default',
    value: '-apple-system, BlinkMacSystemFont, \'Segoe UI\', \'Roboto\', \'Oxygen\', \'Ubuntu\', \'Cantarell\', \'Fira Sans\', \'Droid Sans\', \'Helvetica Neue\', sans-serif',
    preview: 'The quick brown fox',
  },
  {
    name: 'Playfair Display',
    value: '\'Playfair Display\', Georgia, serif',
    googleFont: 'Playfair+Display:wght@400;600;700',
    preview: 'The quick brown fox',
  },
  {
    name: 'Montserrat',
    value: '\'Montserrat\', -apple-system, sans-serif',
    googleFont: 'Montserrat:wght@400;500;600;700',
    preview: 'The quick brown fox',
  },
  {
    name: 'Lora',
    value: '\'Lora\', Georgia, serif',
    googleFont: 'Lora:wght@400;500;600;700',
    preview: 'The quick brown fox',
  },
  {
    name: 'Raleway',
    value: '\'Raleway\', -apple-system, sans-serif',
    googleFont: 'Raleway:wght@400;500;600;700',
    preview: 'The quick brown fox',
  },
  {
    name: 'Cormorant Garamond',
    value: '\'Cormorant Garamond\', Georgia, serif',
    googleFont: 'Cormorant+Garamond:wght@400;500;600;700',
    preview: 'The quick brown fox',
  },
  {
    name: 'Open Sans',
    value: '\'Open Sans\', -apple-system, sans-serif',
    googleFont: 'Open+Sans:wght@400;500;600;700',
    preview: 'The quick brown fox',
  },
  {
    name: 'Crimson Text',
    value: '\'Crimson Text\', Georgia, serif',
    googleFont: 'Crimson+Text:wght@400;600;700',
    preview: 'The quick brown fox',
  },
];

/**
 * ThemePreview Component
 * 
 * Reusable preview section showing how colors will look
 */
const ThemePreview = ({ customColors }) => (
  <div className="space-y-6">
    {/* Primary Color Examples */}
    <div className="bg-white p-4 rounded-lg border border-accent">
      <p className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
        Primary Color Usage
      </p>
      <p className="text-xs text-gray-500 mb-3">
        Active navigation tabs, buttons, table & confirmed badges
      </p>
      <div className="space-y-3">
        {/* Navigation Tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="px-4 py-2 rounded-lg text-sm font-medium shadow-md flex items-center space-x-2"
            style={{
              backgroundColor: customColors.primary,
              color: 'var(--color-surface)',
            }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <span>Venue</span>
          </div>
          <div
            className="px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2"
            style={{ color: customColors.secondary }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Gallery</span>
          </div>
          <div
            className="px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 hidden sm:flex"
            style={{ color: customColors.secondary }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span>Upload</span>
          </div>
        </div>
        {/* Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-600">Badges:</span>
          <span
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md whitespace-nowrap shadow-sm h-7"
            style={{
              backgroundColor: customColors.primary,
              color: 'var(--color-surface)',
              borderColor: customColors.primary,
              borderWidth: '1px',
              borderStyle: 'solid',
            }}
          >
            <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
            Table 3
          </span>
          <span
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md whitespace-nowrap shadow-sm h-7"
            style={{
              backgroundColor: customColors.primary,
              color: 'var(--color-surface)',
              borderColor: customColors.primary,
              borderWidth: '1px',
              borderStyle: 'solid',
            }}
          >
            <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Confirmed
          </span>
        </div>
      </div>
    </div>

    {/* Secondary Color Examples */}
    <div className="bg-white p-4 rounded-lg border border-accent">
      <p className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
        Secondary Color Usage
      </p>
      <p className="text-xs text-gray-500 mb-3">
        All text, headings, and navigation throughout the site
      </p>
      <div className="space-y-3">
        <div>
          <h4 className="text-base font-semibold mb-1" style={{ color: customColors.secondary }}>
            Wedding Celebration
          </h4>
          <p className="text-sm" style={{ color: customColors.secondary }}>
            Join us for a day of love and laughter
          </p>
        </div>
      </div>
    </div>

    {/* Accent Color Examples */}
    <div className="bg-white p-4 rounded-lg border border-accent">
      <p className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
        Accent Color Usage
      </p>
      <p className="text-xs text-gray-500 mb-3">
        Success messages, toast notifications, and video player controls
      </p>
      <div className="space-y-3">
        <div
          className="p-3 rounded-lg border shadow-lg flex items-center gap-2"
          style={{
            backgroundColor: customColors.accent,
            borderColor: customColors.primary,
            color: customColors.secondary
          }}
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs font-medium">
            Theme applied successfully!
          </p>
        </div>
      </div>
    </div>

    {/* Neutral Color Examples */}
    <div className="bg-white p-4 rounded-lg border border-accent">
      <p className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
        Neutral Color Usage
      </p>
      <p className="text-xs text-gray-500 mb-3">
        Status badges and action buttons in venue chart
      </p>
      <div className="space-y-3">
        {/* Not Confirmed Badge */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-600">Status:</span>
          <span
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md whitespace-nowrap shadow-sm h-7"
            style={{
              backgroundColor: customColors.neutral,
              color: customColors.secondary,
              borderColor: customColors.neutral,
              borderWidth: '1px',
              borderStyle: 'solid',
            }}
          >
            <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            Not Confirmed
          </span>
        </div>
      </div>
    </div>

    {/* Upload Form Preview */}
    <div className="bg-white p-4 rounded-lg border border-accent">
      <p className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">
        Upload Section
      </p>
      <div
        className="border-2 border-dashed rounded-lg p-6 text-center"
        style={{ borderColor: customColors.primary }}
      >
        <svg
          className="mx-auto h-12 w-12 mb-3"
          style={{ color: customColors.primary }}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>
        <h3 className="text-base font-semibold mb-1" style={{ color: customColors.secondary }}>
          Drop photos <span className="hidden sm:inline">and videos </span>here
        </h3>
        <p className="text-sm text-gray-600 mb-3 hidden sm:block">
          Capture or share your favourite moments!
        </p>
        <p className="text-sm text-gray-600 mb-3 sm:hidden">
          Share your favourite moments!
        </p>
        <button
          className="px-4 py-2 rounded-lg text-sm font-medium shadow-sm"
          style={{ backgroundColor: customColors.primary, color: 'var(--color-surface)' }}
          disabled
        >
          Choose Photos<span className="hidden sm:inline"> & Videos</span>
        </button>
        <p className="text-xs text-gray-500 mt-3 hidden sm:block">
          Image and Video uploads Only (max 1GB each)
        </p>
      </div>
    </div>
  </div>
);

/**
 * ThemePicker Component
 * 
 * Interactive modal for customizing wedding website colors.
 * Allows users to select from predefined color presets or create custom color schemes.
 * 
 * Props:
 * - onClose: Function to call when modal is closed
 * - onShowToast: Function to show toast notifications
 */
const ThemePicker = ({ onClose, onShowToast }) => {
  // State for which color picker popover is open
  const [openPicker, setOpenPicker] = useState(null);
  const [popoverHexValue, setPopoverHexValue] = useState('');
  // State for mobile preview collapse
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Load current theme from localStorage
  const { loadTheme } = require('../utils/themeStorage');
  const currentThemeData = loadTheme();
  const loadedColors = currentThemeData.colors || currentThemeData; // Handle both formats
  const loadedFont = currentThemeData.font || DEFAULT_FONT;

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is outside any color picker popover
      if (openPicker && !e.target.closest('.color-picker-container')) {
        setOpenPicker(null);
      }
    };

    if (openPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openPicker]);

  // Check if loaded colors match any preset by comparing all 4 colors
  const findMatchingPreset = (colors) => {
    for (const [key, preset] of Object.entries(COLOR_PRESETS)) {
      const presetColors = preset.colors;
      if (
        presetColors.primary === colors.primary &&
        presetColors.secondary === colors.secondary &&
        presetColors.accent === colors.accent &&
        presetColors.neutral === colors.neutral
      ) {
        return key;
      }
    }
    return 'custom';
  };

  const matchedPreset = findMatchingPreset(loadedColors);

  // State for selected preset
  const [selectedPreset, setSelectedPreset] = useState(matchedPreset);

  // State for custom colors - initialize with loaded colors
  const [customColors, setCustomColors] = useState(loadedColors);

  // State for selected font
  const [selectedFont, setSelectedFont] = useState(loadedFont);

  // Check if current customColors match any preset (for dynamic custom card)
  const currentPresetMatch = findMatchingPreset(customColors);
  const showCustomCard = currentPresetMatch === 'custom';

  // Load Google Fonts dynamically
  useEffect(() => {
    const loadGoogleFont = (fontOption) => {
      if (!fontOption.googleFont) return;
      
      const linkId = `google-font-${fontOption.googleFont}`;
      if (document.getElementById(linkId)) return; // Already loaded
      
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${fontOption.googleFont}&display=swap`;
      document.head.appendChild(link);
    };

    // Load all Google Fonts for preview
    FONT_OPTIONS.forEach(loadGoogleFont);
  }, []);

  // Handle Escape key press to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Add event listener and prevent body scroll when modal is open
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    // Cleanup on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  // Handle backdrop click to close modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  /**
   * Validate hex color format
   * Accepts formats: #RGB, #RRGGBB
   */
  const isValidHexColor = (hex) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
  };

  /**
   * Apply theme colors and font to CSS variables
   * Updates the root CSS variables to change the site's color scheme and font
   */
  const applyTheme = (colors, font) => {
    // Use centralized theme utility
    applyThemeUtil({ colors, font });
    // Save to localStorage (ThemePicker-specific behavior)
    saveTheme(colors, font);
  };

  /**
   * Handle Apply Changes button click
   * Applies the current colors and font to the site and shows toast
   */
  const handleApplyAndPreview = () => {
    applyTheme(customColors, selectedFont);

    // Show success toast
    if (onShowToast) {
      onShowToast({ message: 'Theme applied successfully!', type: 'success' });
    }

    // Close modal
    onClose();
  };

  /**
   * Handle reset to default theme
   * Resets to hardcoded default theme (not the config file, which may have been customized)
   * Updates preview only (doesn't apply to site)
   */
  const handleReset = () => {
    // Use hardcoded default theme values, not the config file
    // This ensures "Reset to Default" always goes back to the original defaults
    // regardless of what's in the config file or bundled config
    const defaultColors = DEFAULT_THEME;

    // Update preview colors and font only (don't apply or clear localStorage yet)
    setCustomColors(defaultColors);
    setSelectedFont(DEFAULT_FONT);

    // Detect which preset matches the default colors
    const detectedPreset = findMatchingPreset(defaultColors);
    setSelectedPreset(detectedPreset);
  };

  /**
   * Handle preset selection
   * Only updates preview, doesn't apply to site
   */
  const handlePresetClick = (presetKey) => {
    setSelectedPreset(presetKey);
    const presetColors = COLOR_PRESETS[presetKey].colors;
    setCustomColors(presetColors);
  };

  /**
   * Handle custom color change from color picker
   * Updates state for preview only
   */
  const handleColorPickerChange = (colorKey, value) => {
    const newColors = { ...customColors, [colorKey]: value };
    setCustomColors(newColors);
    setSelectedPreset('custom');
  };

  const handleOpenPicker = (colorKey) => {
    if (openPicker === colorKey) {
      setOpenPicker(null);
    } else {
      setOpenPicker(colorKey);
      setPopoverHexValue(customColors[colorKey]);
    }
  };

  const handleApplyColorFromPopover = () => {
    if (isValidHexColor(popoverHexValue)) {
      handleColorPickerChange(openPicker, popoverHexValue);
      setOpenPicker(null);
    }
  };
  
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="theme-picker-title"
    >
      {/* Modal Container */}
      <div
        className="relative bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto custom-scrollbar"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: selectedFont }}
      >
        {/* Header with Close Button */}
        <div className="sticky top-0 bg-white border-b border-accent px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2
              id="theme-picker-title"
              className="text-2xl font-semibold text-secondary"
            >
              Customize Your Wedding Website Colors
            </h2>
            <p className="text-sm text-gray-600 mt-1 hidden md:block">
              Pick from ready-made color schemes or create your own. See changes instantly as you explore different options!
            </p>
            <p className="text-sm text-gray-600 mt-1 md:hidden">
              Choose colors and fonts for your wedding website
            </p>
          </div>

          {/* Close Button */}
          <IconButton onClick={onClose} label="Close theme picker" className="ml-4 min-w-[44px] min-h-[44px]">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </IconButton>
        </div>

        {/* Modal Content */}
        <div className="px-6 py-6 md:grid md:grid-cols-10 md:gap-8">
          {/* Left Column - Settings (60%) */}
          <div className="md:col-span-6 md:pr-4 md:border-r md:border-accent">
            {/* Color Presets Section */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-secondary mb-2">
                Step 1: Choose a Color Scheme
              </h3>
              <p className="text-sm text-gray-600 mb-4 hidden md:block">
                Start with one of our professionally designed color combinations. Each scheme includes four coordinated colors that work beautifully together.
              </p>

              {/* Preset Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Standard presets */}
                {Object.entries(COLOR_PRESETS).map(([key, preset]) => (
                  <PresetButton
                    key={key}
                    onClick={() => handlePresetClick(key)}
                    preset={preset}
                    isSelected={selectedPreset === key}
                    customColors={customColors}
                  />
                ))}

                {/* Show Custom preset card at the end if current colors don't match any preset */}
                {showCustomCard && (
                  <PresetButton
                    onClick={() => setSelectedPreset('custom')}
                    preset={{ name: 'Custom Colors', colors: customColors }}
                    isSelected={selectedPreset === 'custom'}
                    customColors={customColors}
                  />
                )}
              </div>
            </section>

            {/* Font Picker Section */}
            <section className="mb-8 border-t-2 border-accent pt-8">
              <h3 className="text-lg font-semibold text-secondary mb-2">
                Step 2: Choose Your Font
              </h3>
              <p className="text-sm text-gray-600 mb-4 hidden md:block">
                Select a font that matches your wedding style. This will apply to all text across your website.
              </p>

              {/* Font Dropdown */}
              <Listbox value={selectedFont} onChange={setSelectedFont}>
                <div className="relative">
                  <Listbox.Button className="w-full px-3 py-2 sm:px-4 sm:py-3 pr-8 sm:pr-10 border-2 border-accent rounded-lg bg-white text-secondary font-medium text-sm sm:text-base text-left
                    focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                    hover:border-gray-400 transition-colors cursor-pointer"
                    style={{ fontFamily: selectedFont }}
                  >
                    <span className="block truncate">
                      {FONT_OPTIONS.find(f => f.value === selectedFont)?.name || 'System Default'}
                    </span>
                    <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </Listbox.Button>
                  <Listbox.Options className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg bg-white py-1 shadow-lg border-2 border-accent focus:outline-none">
                    {FONT_OPTIONS.map((fontOption) => (
                      <Listbox.Option
                        key={fontOption.name}
                        value={fontOption.value}
                        className={({ active }) =>
                          `cursor-pointer select-none relative py-2 px-3 sm:px-4 ${
                            active ? 'bg-primary text-surface' : 'text-secondary'
                          }`
                        }
                        style={{ fontFamily: fontOption.value }}
                      >
                        {({ selected }) => (
                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                            {fontOption.name}
                          </span>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </Listbox>

              {/* Font Preview */}
              <div className="mt-4 p-3 sm:p-4 bg-white border-2 border-accent rounded-lg">
                <p className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                  Font Preview
                </p>
                <div style={{ fontFamily: selectedFont, color: customColors.secondary }}>
                  <p className="text-xl sm:text-2xl font-semibold mb-2">Wedding Celebration</p>
                  <p className="text-sm sm:text-base mb-2">Join us for a day of love and laughter</p>
                  <p className="text-xs sm:text-sm">Saturday, June 15th, 2024 at 4:00 PM</p>
                </div>
              </div>
            </section>

            {/* Custom Color Picker Section */}
            <section className="mb-8 border-t-2 border-accent pt-8">
              <h3 className="text-lg font-semibold text-secondary mb-2">
                Step 3: Fine-Tune Your Colors <span className="text-sm font-normal">(Optional)</span>
              </h3>
              <p className="text-sm text-gray-600 mb-4 hidden md:block">
                Want to adjust the colors? Click any color box below or type in a specific color code. Your changes appear instantly on the website!
              </p>

              {/* Color Picker Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Surface Color */}
                <div className="relative color-picker-container">
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Surface Color
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Background, cards, modals (White Recommended)
                  </p>
                  <div className="flex items-center gap-3 p-3 border-2 border-accent rounded-lg hover:border-accent transition-colors bg-white">
                    {/* Color Swatch - Clickable */}
                    <button
                      type="button"
                      onClick={() => handleOpenPicker('surface')}
                      className="cursor-pointer relative group flex-shrink-0"
                      aria-label="Open surface color picker"
                    >
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-accent shadow-sm hover:scale-105 transition-transform relative"
                        style={{ backgroundColor: customColors.surface || '#ffffff' }}
                      >
                        {/* Palette Icon Overlay */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg">
                          <svg className="w-5 h-5 text-surface opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </button>
                    {/* Hex Input */}
                    <input
                      type="text"
                      value={customColors.surface || '#ffffff'}
                      readOnly
                      placeholder="#ffffff"
                      maxLength="7"
                      className="flex-1 min-w-0 px-3 py-2 border-2 border-accent rounded-lg font-mono text-sm uppercase bg-gray-100 text-gray-500 cursor-default focus:outline-none"
                      aria-label="Surface color hex code"
                    />
                  </div>
                  {/* Color Picker Popover */}
                  {openPicker === 'surface' && (
                    <div className="absolute z-10 mt-2 p-3 bg-white rounded-lg shadow-xl border border-accent">
                      <HexColorPicker
                        color={popoverHexValue}
                        onChange={setPopoverHexValue}
                        style={{ width: '200px', height: '150px' }}
                      />
                      <input
                        type="text"
                        value={popoverHexValue}
                        onChange={(e) => setPopoverHexValue(e.target.value.toUpperCase())}
                        placeholder="#RRGGBB"
                        maxLength="7"
                        className="w-full mt-2 px-2 py-1 border-2 rounded-md font-mono text-sm uppercase"
                      />
                      <div className="flex gap-2 mt-2">
                        <Button
                          onClick={handleApplyColorFromPopover}
                          variant="primary"
                          size="sm"
                          className="flex-1"
                          disabled={!isValidHexColor(popoverHexValue)}
                        >
                          Apply
                        </Button>
                        <Button
                          onClick={() => setOpenPicker(null)}
                          variant="neutral"
                          size="sm"
                          className="flex-1"
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Primary Color */}
                <div className="relative color-picker-container">
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Primary Color
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Buttons, links, interactive elements
                  </p>
                  <div className="flex items-center gap-3 p-3 border-2 border-accent rounded-lg hover:border-accent transition-colors bg-white">
                    {/* Color Swatch - Clickable */}
                    <button
                      type="button"
                      onClick={() => handleOpenPicker('primary')}
                      className="cursor-pointer relative group flex-shrink-0"
                      aria-label="Open primary color picker"
                    >
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-accent shadow-sm hover:scale-105 transition-transform relative"
                        style={{ backgroundColor: customColors.primary }}
                      >
                        {/* Palette Icon Overlay */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg">
                          <svg className="w-5 h-5 text-surface opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </button>
                    {/* Hex Input */}
                    <input
                      type="text"
                      value={customColors.primary}
                      readOnly
                      placeholder="#8EA58C"
                      maxLength="7"
                      className="flex-1 min-w-0 px-3 py-2 border-2 border-accent rounded-lg font-mono text-sm uppercase bg-gray-100 text-gray-500 cursor-default focus:outline-none"
                      aria-label="Primary color hex code"
                    />
                  </div>
                  {/* Color Picker Popover */}
                  {openPicker === 'primary' && (
                    <div className="absolute z-10 mt-2 p-3 bg-white rounded-lg shadow-xl border border-accent">
                      <HexColorPicker 
                        color={popoverHexValue} 
                        onChange={setPopoverHexValue}
                        style={{ width: '200px', height: '150px' }}
                      />
                      <input
                        type="text"
                        value={popoverHexValue}
                        onChange={(e) => setPopoverHexValue(e.target.value.toUpperCase())}
                        placeholder="#RRGGBB"
                        maxLength="7"
                        className="w-full mt-2 px-2 py-1 border-2 rounded-md font-mono text-sm uppercase"
                      />
                      <div className="flex gap-2 mt-2">
                        <Button
                          onClick={handleApplyColorFromPopover}
                          variant="primary"
                          size="sm"
                          className="flex-1"
                          disabled={!isValidHexColor(popoverHexValue)}
                        >
                          Apply
                        </Button>
                        <Button
                          onClick={() => setOpenPicker(null)}
                          variant="neutral"
                          size="sm"
                          className="flex-1"
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Secondary Color */}
                <div className="relative color-picker-container">
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Secondary Color
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Text, headings throughout the site
                  </p>
                  <div className="flex items-center gap-3 p-3 border-2 border-accent rounded-lg hover:border-accent transition-colors bg-white">
                    {/* Color Swatch - Clickable */}
                    <button
                      type="button"
                      onClick={() => handleOpenPicker('secondary')}
                      className="cursor-pointer relative group flex-shrink-0"
                      aria-label="Open secondary color picker"
                    >
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-accent shadow-sm hover:scale-105 transition-transform relative"
                        style={{ backgroundColor: customColors.secondary }}
                      >
                        {/* Palette Icon Overlay */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg">
                          <svg className="w-5 h-5 text-surface opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </button>
                    {/* Hex Input */}
                    <input
                      type="text"
                      value={customColors.secondary}
                      readOnly
                      placeholder="#5C4033"
                      maxLength="7"
                      className="flex-1 min-w-0 px-3 py-2 border-2 border-accent rounded-lg font-mono text-sm uppercase bg-gray-100 text-gray-500 cursor-default focus:outline-none"
                      aria-label="Secondary color hex code"
                    />
                  </div>
                  {/* Color Picker Popover */}
                  {openPicker === 'secondary' && (
                    <div className="absolute z-10 mt-2 p-3 bg-white rounded-lg shadow-xl border border-accent">
                      <HexColorPicker 
                        color={popoverHexValue} 
                        onChange={setPopoverHexValue}
                        style={{ width: '200px', height: '150px' }}
                      />
                      <input
                        type="text"
                        value={popoverHexValue}
                        onChange={(e) => setPopoverHexValue(e.target.value.toUpperCase())}
                        placeholder="#RRGGBB"
                        maxLength="7"
                        className="w-full mt-2 px-2 py-1 border-2 rounded-md font-mono text-sm uppercase"
                      />
                      <div className="flex gap-2 mt-2">
                        <Button
                          onClick={handleApplyColorFromPopover}
                          variant="primary"
                          size="sm"
                          className="flex-1"
                          disabled={!isValidHexColor(popoverHexValue)}
                        >
                          Apply
                        </Button>
                        <Button
                          onClick={() => setOpenPicker(null)}
                          variant="neutral"
                          size="sm"
                          className="flex-1"
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Accent Color */}
                <div className="relative color-picker-container">
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Accent Color
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Borders, highlights, decorative touches
                  </p>
                  <div className="flex items-center gap-3 p-3 border-2 border-accent rounded-lg hover:border-accent transition-colors bg-white">
                    {/* Color Swatch - Clickable */}
                    <button
                      type="button"
                      onClick={() => handleOpenPicker('accent')}
                      className="cursor-pointer relative group flex-shrink-0"
                      aria-label="Open accent color picker"
                    >
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-accent shadow-sm hover:scale-105 transition-transform relative"
                        style={{ backgroundColor: customColors.accent }}
                      >
                        {/* Palette Icon Overlay */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg">
                          <svg className="w-5 h-5 text-surface opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </button>
                    {/* Hex Input */}
                    <input
                      type="text"
                      value={customColors.accent}
                      readOnly
                      placeholder="#BFCFBB"
                      maxLength="7"
                      className="flex-1 min-w-0 px-3 py-2 border-2 border-accent rounded-lg font-mono text-sm uppercase bg-gray-100 text-gray-500 cursor-default focus:outline-none"
                      aria-label="Accent color hex code"
                    />
                  </div>
                  {/* Color Picker Popover */}
                  {openPicker === 'accent' && (
                    <div className="absolute z-10 mt-2 p-3 bg-white rounded-lg shadow-xl border border-accent">
                      <HexColorPicker 
                        color={popoverHexValue} 
                        onChange={setPopoverHexValue}
                        style={{ width: '200px', height: '150px' }}
                      />
                      <input
                        type="text"
                        value={popoverHexValue}
                        onChange={(e) => setPopoverHexValue(e.target.value.toUpperCase())}
                        placeholder="#RRGGBB"
                        maxLength="7"
                        className="w-full mt-2 px-2 py-1 border-2 rounded-md font-mono text-sm uppercase"
                      />
                      <div className="flex gap-2 mt-2">
                        <Button
                          onClick={handleApplyColorFromPopover}
                          variant="primary"
                          size="sm"
                          className="flex-1"
                          disabled={!isValidHexColor(popoverHexValue)}
                        >
                          Apply
                        </Button>
                        <Button
                          onClick={() => setOpenPicker(null)}
                          variant="neutral"
                          size="sm"
                          className="flex-1"
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Neutral Color */}
                <div className="relative color-picker-container">
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Neutral Color
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Subtle design elements and guest status badges
                  </p>
                  <div className="flex items-center gap-3 p-3 border-2 border-accent rounded-lg hover:border-accent transition-colors bg-white">
                    {/* Color Swatch - Clickable */}
                    <button
                      type="button"
                      onClick={() => handleOpenPicker('neutral')}
                      className="cursor-pointer relative group flex-shrink-0"
                      aria-label="Open neutral color picker"
                    >
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-accent shadow-sm hover:scale-105 transition-transform relative"
                        style={{ backgroundColor: customColors.neutral }}
                      >
                        {/* Palette Icon Overlay */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg">
                          <svg className="w-5 h-5 text-surface opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </button>
                    {/* Hex Input */}
                    <input
                      type="text"
                      value={customColors.neutral}
                      readOnly
                      placeholder="#D9C2A8"
                      maxLength="7"
                      className="flex-1 min-w-0 px-3 py-2 border-2 border-accent rounded-lg font-mono text-sm uppercase bg-gray-100 text-gray-500 cursor-default focus:outline-none"
                      aria-label="Neutral color hex code"
                    />
                  </div>
                  {/* Color Picker Popover */}
                  {openPicker === 'neutral' && (
                    <div className="absolute z-10 mt-2 p-3 bg-white rounded-lg shadow-xl border border-accent">
                      <HexColorPicker 
                        color={popoverHexValue} 
                        onChange={setPopoverHexValue}
                        style={{ width: '200px', height: '150px' }}
                      />
                      <input
                        type="text"
                        value={popoverHexValue}
                        onChange={(e) => setPopoverHexValue(e.target.value.toUpperCase())}
                        placeholder="#RRGGBB"
                        maxLength="7"
                        className="w-full mt-2 px-2 py-1 border-2 rounded-md font-mono text-sm uppercase"
                      />
                      <div className="flex gap-2 mt-2">
                        <Button
                          onClick={handleApplyColorFromPopover}
                          variant="primary"
                          size="sm"
                          className="flex-1"
                          disabled={!isValidHexColor(popoverHexValue)}
                        >
                          Apply
                        </Button>
                        <Button
                          onClick={() => setOpenPicker(null)}
                          variant="neutral"
                          size="sm"
                          className="flex-1"
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Preset Indicator */}
              {selectedPreset === 'custom' && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">✨ Custom Colors:</span> You've created your own unique color combination!
                  </p>
                </div>
              )}
            </section>
          </div>

          {/* Right Column - Preview (Desktop only, 40%) */}
          <div className="hidden md:block md:col-span-4 md:pl-4">
            {/* Live Preview Section */}
            <section className="mb-8 md:border-t-0 border-t-2 border-accent md:pt-0 pt-8">
              <h3 className="text-lg font-semibold text-secondary mb-2">
                Step 4: Preview Your Theme
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Here's how your chosen colors will look on your website. The entire site updates in real-time as you make changes!
              </p>

              <ThemePreview customColors={customColors} />
            </section>
          </div>

          {/* Mobile Preview Section - Collapsible */}
          <div className="md:hidden md:col-span-6 border-t-2 border-accent pt-8 mb-8">
            <button
              onClick={() => setIsPreviewOpen(!isPreviewOpen)}
              className="w-full flex items-center justify-between text-left mb-4"
            >
              <h3 className="text-lg font-semibold text-secondary">
                Step 4: Preview Your Theme
              </h3>
              <svg
                className={`w-5 h-5 text-secondary transition-transform ${isPreviewOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {isPreviewOpen && <ThemePreview customColors={customColors} />}
          </div>

          {/* Export Section - Full Width */}
          <div className="md:col-span-10 md:border-t-2 md:border-accent md:pt-8 border-t-2 border-accent pt-8">
            <section>
              <h3 className="text-lg font-semibold text-secondary mb-2">
                Step 5: Apply Your Theme
              </h3>
              <p className="text-sm text-gray-600 mb-6 hidden md:block">
                Love your colors? Click "Apply Changes" to see how the website looks with your chosen colors!
              </p>

              <div className="space-y-4">
                {/* 
                  IMPORTANT: These buttons MUST use customColors (not CSS variables) for live preview!
                  - customColors updates when browsing themes (live preview in modal only)
                  - CSS variables (var(--color-*)) only update when "Apply Changes" is clicked
                  DO NOT CHANGE to use var(--color-primary) or var(--color-accent)!
                */}
                {/* Apply Changes and Reset Buttons in One Row */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={handleApplyAndPreview}
                    variant="primary"
                    size="lg"
                    className="flex-1"
                    style={{ backgroundColor: customColors.primary, borderColor: customColors.primary }}
                    icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                  >
                    Apply Changes
                  </Button>
                  <Button
                    onClick={handleReset}
                    variant="accent"
                    size="lg"
                    className="flex-1"
                    style={{ backgroundColor: customColors.accent, borderColor: customColors.accent, color: customColors.secondary }}
                    icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>}
                  >
                    Reset to Default
                  </Button>
                </div>

                {/* Instructions */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Next Steps
                  </h4>
                  <ol className="text-sm text-blue-800 space-y-2 ml-1">
                    <li className="flex gap-2">
                      <span className="font-semibold flex-shrink-0">1.</span>
                      <span>Click "Apply Changes" to see your colors on the website</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold flex-shrink-0">2.</span>
                      <span>When you're happy, close this modal and click "Export Config" in the header</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold flex-shrink-0">3.</span>
                      <span>Send the exported ZIP file to your website developer</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold flex-shrink-0">4.</span>
                      <span>Your custom theme will be live for all your guests to see!</span>
                    </li>
                  </ol>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemePicker;

